import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import CancelIcon from '@material-ui/icons/Cancel';
import GetAppIcon from '@material-ui/icons/GetApp';
import CloseIcon from '@material-ui/icons/Close';
import { retryExportJob, cancelExportJob } from '../../services/exportJobRegistry';
import { formatBytes, elapsedLabel } from './downloadFormat';

const useStyles = makeStyles(() => ({
    panel: {
        maxHeight: 460,
        overflowY: 'auto',
        background: '#fff',
        borderRadius: 10,
        boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
        border: '1px solid #eee',
        zIndex: 1400,
        textAlign: 'left',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        padding: '14px 16px 10px',
        borderBottom: '1px solid #f0f0f0',
        position: 'sticky',
        top: 0,
        background: '#fff',
    },
    title: {
        fontSize: 16,
        fontWeight: 700,
        color: '#222',
        margin: 0,
    },
    subtitle: {
        fontSize: 12.5,
        color: '#888',
        marginTop: 2,
    },
    closeBtn: {
        cursor: 'pointer',
        color: '#999',
        background: 'none',
        border: 'none',
        padding: 4,
        lineHeight: 0,
    },
    empty: {
        padding: '28px 16px',
        textAlign: 'center',
        color: '#999',
        fontSize: 13,
    },
    row: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '12px 16px',
        borderBottom: '1px solid #f5f5f5',
    },
    rowIcon: {
        fontSize: 20,
        marginTop: 2,
        flexShrink: 0,
    },
    completedIcon: { color: '#2e7d32' },
    failedIcon: { color: '#c62828' },
    cancelledIcon: { color: '#9aa5ab' },
    inProgressIcon: { color: '#0d47a1' },
    rowBody: {
        flex: 1,
        minWidth: 0,
    },
    rowTitle: {
        fontSize: 13.5,
        fontWeight: 700,
        color: '#222',
        display: 'block',
    },
    rowSubtitle: {
        fontSize: 12,
        color: '#888',
        marginTop: 2,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    rowSubtitleError: {
        color: '#c62828',
    },
    progressTrack: {
        marginTop: 8,
        height: 4,
        borderRadius: 2,
        background: '#eef2f6',
        overflow: 'hidden',
    },
    progressFill: {
        height: '100%',
        borderRadius: 2,
        background: '#0d47a1',
        transition: 'width .3s ease',
    },
    rowSide: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 6,
        flexShrink: 0,
    },
    statusCompleted: { color: '#2e7d32', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap' },
    statusFailed: { color: '#c62828', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap' },
    statusCancelled: { color: '#7a8a99', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap' },
    statusInProgress: { color: '#0d47a1', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap' },
    actionBtn: {
        fontSize: 12,
        fontWeight: 700,
        padding: '4px 10px',
        borderRadius: 6,
        border: 'none',
        cursor: 'pointer',
        color: '#fff',
        background: '#222',
    },
    retryBtn: {
        background: '#c62828',
    },
    cancelBtn: {
        background: '#fff',
        color: '#7a8a99',
        border: '1px solid #d8dee3',
    },
    // Dark theme overrides, added to the panel root only when the app toggle is off.
    // The title needs none: it is a <p>, which App.css colours with --appTextColor.
    dark: {
        background: '#333',
        border: '1px solid #474747',
        boxShadow: '0 8px 28px rgba(0,0,0,0.55)',
        '& $header': { background: '#333', borderBottom: '1px solid #444' },
        '& $subtitle': { color: '#aaa' },
        '& $closeBtn': { color: '#aaa' },
        '& $empty': { color: '#aaa' },
        '& $row': { borderBottom: '1px solid #3e3e3e' },
        '& $completedIcon': { color: '#66bb6a' },
        '& $failedIcon': { color: '#ef9a9a' },
        '& $inProgressIcon': { color: '#64b5f6' },
        '& $rowTitle': { color: '#eee' },
        '& $rowSubtitle': { color: '#aaa' },
        '& $rowSubtitleError': { color: '#ef9a9a' },
        '& $progressTrack': { background: '#4a4a4a' },
        '& $progressFill': { background: '#64b5f6' },
        '& $statusCompleted': { color: '#66bb6a' },
        '& $statusFailed': { color: '#ef9a9a' },
        '& $statusCancelled': { color: '#9aa5ab' },
        '& $statusInProgress': { color: '#64b5f6' },
        '& $actionBtn': { color: '#222', background: '#eee' },
        '& $retryBtn': { color: '#fff', background: '#c62828' },
        '& $cancelBtn': { color: '#bbb', background: 'transparent', border: '1px solid #5a5a5a' },
    },
}));

function JobRow({ job }) {
    const classes = useStyles();

    const handleDownload = () => {
        if (job.reportUrl) {
            window.open(job.reportUrl, '_blank');
            return;
        }
        if (!job.downloadUrl) return;
        const link = document.createElement('a');
        link.href = job.downloadUrl;
        link.setAttribute('download', job.fileName || '');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleRetry = () => retryExportJob(job.id);
    const handleCancel = () => cancelExportJob(job.id);

    let icon = <GetAppIcon className={`${classes.rowIcon} ${classes.inProgressIcon}`} />;
    if (job.status === 'completed') icon = <CheckCircleIcon className={`${classes.rowIcon} ${classes.completedIcon}`} />;
    if (job.status === 'failed') icon = <ErrorIcon className={`${classes.rowIcon} ${classes.failedIcon}`} />;
    if (job.status === 'cancelled') icon = <CancelIcon className={`${classes.rowIcon} ${classes.cancelledIcon}`} />;

    let subtitle;
    if (job.status === 'completed') {
        const sizeLabel = job.totalBytes ? ` · ${formatBytes(job.totalBytes)}` : '';
        subtitle = `${job.fileName || ''}${sizeLabel} · ${elapsedLabel(job)}`;
    } else if (job.status === 'failed') {
        subtitle = job.error;
    } else if (job.status === 'cancelled') {
        subtitle = `Cancelled after ${elapsedLabel(job)}`;
    } else {
        const sizeLabel = job.totalBytes
            ? `${formatBytes(job.loadedBytes)} of ${formatBytes(job.totalBytes)}`
            : (job.loadedBytes ? formatBytes(job.loadedBytes) : '');
        subtitle = [job.step, sizeLabel, elapsedLabel(job)].filter(Boolean).join(' · ');
    }

    return (
        <div className={classes.row}>
            {icon}
            <div className={classes.rowBody}>
                <span className={classes.rowTitle}>{job.name}</span>
                <div className={`${classes.rowSubtitle} ${job.status === 'failed' ? classes.rowSubtitleError : ''}`}>
                    {subtitle}
                </div>
                {job.status === 'in-progress' && (
                    <div className={classes.progressTrack}>
                        <div className={classes.progressFill} style={{ width: `${job.percent != null ? job.percent : 15}%` }} />
                    </div>
                )}
            </div>
            <div className={classes.rowSide}>
                {job.status === 'completed' && (
                    <>
                        <span className={classes.statusCompleted}>Completed</span>
                        <button type="button" className={classes.actionBtn} onClick={handleDownload}>{job.reportUrl ? 'View Report' : 'Download'}</button>
                    </>
                )}
                {job.status === 'failed' && (
                    <>
                        <span className={classes.statusFailed}>Failed</span>
                        <button type="button" className={`${classes.actionBtn} ${classes.retryBtn}`} onClick={handleRetry}>Retry</button>
                    </>
                )}
                {job.status === 'cancelled' && (
                    <>
                        <span className={classes.statusCancelled}>Cancelled</span>
                        <button type="button" className={classes.actionBtn} onClick={handleRetry}>Retry</button>
                    </>
                )}
                {job.status === 'in-progress' && (
                    <>
                        <span className={classes.statusInProgress}>
                            {job.percent != null ? `${job.percent}%` : 'In progress'}
                        </span>
                        <button type="button" className={`${classes.actionBtn} ${classes.cancelBtn}`} onClick={handleCancel}>Cancel</button>
                    </>
                )}
            </div>
        </div>
    );
}

export default function DownloadsPanel({ jobs, onClose, style }) {
    const classes = useStyles();
    const toggle = useSelector((state) => state.setToggle.toggle);
    const activeCount = jobs.filter((j) => j.status === 'in-progress').length;

    // Re-render every second so elapsed-time labels stay live while jobs are running.
    const [, setTick] = useState(0);
    useEffect(() => {
        if (activeCount === 0) return;
        const interval = setInterval(() => setTick((t) => t + 1), 1000);
        return () => clearInterval(interval);
    }, [activeCount]);

    return (
        <div className={`${classes.panel} ${toggle ? '' : classes.dark}`} style={style} onClick={(e) => e.stopPropagation()}>
            <div className={classes.header}>
                <div>
                    <p className={classes.title}>Downloads</p>
                    <div className={classes.subtitle}>
                        {activeCount > 0
                            ? `${activeCount} export${activeCount === 1 ? '' : 's'} in progress`
                            : 'No downloads in progress'}
                    </div>
                </div>
                <button type="button" className={classes.closeBtn} onClick={onClose} aria-label="Close">
                    <CloseIcon fontSize="small" />
                </button>
            </div>
            {jobs.length === 0
                ? <div className={classes.empty}>No downloads yet</div>
                : jobs.map((job) => <JobRow key={job.id} job={job} />)}
        </div>
    );
}
